/**
 * SEO + JSON-LD pour la page /b/[slug] (billetterie événement).
 * Sort le bloc useHead + useSeoMeta du composant index.vue pour réduire la taille.
 *
 * SEO sprint v2 (Karim 2026-06-15) :
 * - noindex sur 404 / draft / canceled / archived (évite polluer l'index Google
 *   avec des pages inexistantes/non-publiques)
 * - JSON-LD Event enrichi : organizer = vraie asso (pas "Donoor" générique),
 *   offers (prix minimum + currency + availability + url) — débloque les
 *   Rich Snippets Event dans la SERP (date + prix visibles)
 * - BreadcrumbList JSON-LD ajouté
 * - title + description enrichis avec date FR + ville pour mieux ranker sur
 *   les requêtes type "concert paris 15 juin"
 */
import type { Event, TicketType, EventStatus } from "~/types/event";
import { formatDateAssociation, fuseauDe, isoAvecDecalage } from "~/utils/heureAssociation";
import { getDirectusFileId } from "@donoor/types/payment";
import { analyserAdresseLibre, recusFiscauxDansPays, texteLieu } from "@donoor/utils";
import { adressePostaleSchema, geoLieuSchema, villeCourteLieu } from "#shared/utils/lieuPublic";
import { LOCALE_INTL, type Langue } from "~/utils/langue";
import { useDemoAccueil } from "~/composables/useDemoAccueil";

/**
 * Adresse JSON-LD d'un lieu (§10 du contrat des adresses structurées) :
 * `PostalAddress` depuis les MORCEAUX (rue déclarée, code postal, ville,
 * territoire, pays réel — fin du « FR » en dur), à défaut l'analyse du texte.
 * L'analyse lit le texte avec le pays du lieu, sinon celui de l'association
 * (`paysParDefaut`, même lecture que l'API) : « Rue Neuve 1, 1000 Bruxelles »
 * d'une association belge donne bien le code postal 1000 et la Belgique.
 * Mise en forme : `adressePostaleSchema`, la même que les hubs de l'annuaire.
 */
function adresseLieuSchema(e: Event, adresse: string): Record<string, unknown> | null {
  const paysParDefaut = e.location_country || e.country || null;
  const analyse = analyserAdresseLibre(texteLieu(adresse, e.location_city, { paysParDefaut }), { paysParDefaut });
  return adressePostaleSchema({
    rue: e.location_street || analyse.rue || adresse,
    codePostal: e.location_postcode || analyse.codePostal,
    ville: e.location_city || analyse.ville,
    territoire: e.location_territory ?? analyse.territoire,
    pays: e.location_country || analyse.pays,
  });
}

/**
 * Lieu JSON-LD d'un événement : nom, adresse structurée et point. Tolère
 * l'absence de tous les champs 307 (site publié avant l'API, anciens lieux).
 * Exporté pour les tests.
 */
export function lieuEvenementSchema(e: Event): Record<string, unknown> | null {
  if (!e.location_name && !e.location_address) return null;
  const geo = geoLieuSchema(e);
  const address = e.location_address ? adresseLieuSchema(e, e.location_address) : null;
  return {
    "@type": "Place",
    name: e.location_name || e.location_city || "",
    ...(address ? { address } : {}),
    ...(geo ? { geo } : {}),
  };
}

export function useEventCheckoutSeo(
  event: Ref<Event | null>,
  slug: Ref<string>,
) {
  const { t, locale } = useI18n();
  // og:locale au format underscore ("en_GB"…) attendu par Facebook/LinkedIn —
  // suit la langue de la page au lieu d'être figé en français.
  const ogLocale = computed(() => (LOCALE_INTL[locale.value as Langue] ?? LOCALE_INTL.fr).replace("-", "_"));
  const config = useRuntimeConfig();
  // Origine du site COURANT (prod/staging/préview) pour les og:image
  // auto-référentes — cf. useShareBaseUrl. Canonical reste prod en dur.
  const shareBase = useShareBaseUrl();

  const ogDescription = computed(() => {
    if (!event.value) return "";
    const raw = event.value.description?.replace(/<[^>]*>/g, "").trim() || "";
    if (raw) return raw.substring(0, 160);
    // Fallback construit : titre + ville + date (utile quand description vide).
    // Billetterie clôturée → message cohérent (pas de « Réservez vos billets »).
    // Karim 2026-06-27.
    const parts: string[] = [];
    const closed = event.value.status === "closed";
    if (event.value.title) {
      parts.push(
        closed
          ? t("billetterie.useEventCheckoutSeo.billetterieCloturee", { titre: event.value.title })
          : t("billetterie.useEventCheckoutSeo.reservezVosBillets", { titre: event.value.title }),
      );
    }
    // Ville du lieu (§10) : « à Saint-Denis, La Réunion », pas l'homonyme du 93.
    const ville = villeCourteLieu(event.value);
    if (ville) parts.push(t("billetterie.useEventCheckoutSeo.aVille", { ville }));
    const dateStr = formatDateFr(event.value.start_date, fuseauDe(event.value));
    if (dateStr) parts.push(t("billetterie.useEventCheckoutSeo.leDate", { date: dateStr }));
    // Hors France (chantier « autres pays ») : pas de reçu fiscal à promettre.
    // Et seulement si l'événement ouvre vraiment droit à un reçu (31/10/2026) :
    // avant, toute page d'événement annonçait « Reçu fiscal automatique ».
    parts.push(
      closed
        ? t("billetterie.useEventCheckoutSeo.inscriptionsFermees")
        : recusFiscauxDansPays(event.value.country) && event.value.is_tax_deductible === true
          ? t("billetterie.useEventCheckoutSeo.paiementSecuriseRecu")
          : t("billetterie.useEventCheckoutSeo.paiementSecurise"),
    );
    return parts.join(" ").substring(0, 160);
  });

  const eventCoverImageId = computed(() => getDirectusFileId(event.value?.cover_image));

  // OG image dynamique (sprint v4 Karim 2026-06-15) : route /api/og/event qui
  // génère un PNG rose 1200×630 avec titre + date + ville + prix dès + asso.
  // CTR partage social ~30% supérieur à la simple cover_image hero.
  // Fallback cover_image puis og-image.png si le slug est inconnu.
  const ogImageUrl = computed(() => {
    if (slug.value) return `${shareBase}/api/og/event?slug=${slug.value}`;
    if (eventCoverImageId.value) {
      return `${config.public.apiUrl}/api/assets/${eventCoverImageId.value}?width=1200&height=630&fit=cover&format=jpg&quality=80`;
    }
    return `${shareBase}/og-image.png`;
  });

  const canonicalUrl = computed(() => `https://donoor.org/b/${slug.value}`);

  // Title enrichi : "{Titre} — {Asso} — {Ville}" (Karim 2026-08-04).
  // AVANT : "{Titre} — {Ville} — {Date}" — le NOM DE L'ASSO était ABSENT, donc
  // l'event ne ressortait pas sur « billetterie {asso} » ni « {asso} événement »
  // (même problème que les cagnottes). On ajoute le nom de l'asso juste après
  // le titre (signal fort), puis la ville. La date passe en second plan (cap
  // 60 car. ; on privilégie asso+ville aux requêtes géo-temporelles).
  // Pas de suffixe « | Donoor » : titleTemplate global "%s".
  const seoTitle = computed(() => {
    if (!event.value) return t("billetterie.useEventCheckoutSeo.evenementParDefaut");
    const parts: string[] = [event.value.title];
    const org = event.value.organization_name?.trim();
    // On n'ajoute l'asso que si son nom n'est pas déjà dans le titre saisi.
    if (org && !event.value.title.toLowerCase().includes(org.toLowerCase())) {
      parts.push(org);
    }
    const ville = villeCourteLieu(event.value);
    if (ville) parts.push(ville);
    const dateStr = formatDateFr(event.value.start_date, fuseauDe(event.value));
    if (dateStr) parts.push(dateStr);
    return parts.join(" — ").substring(0, 60);
  });

  // Noindex si l'événement n'existe pas, est en brouillon, annulé ou archivé.
  // Le SSR retourne aussi un 404 HTTP pour les vraies absences (cf. logique
  // dans la page) — ici on bloque juste l'indexation côté meta.
  const shouldNoindex = computed(() => {
    if (!event.value) return true;
    // Événement PRIVÉ (migration 285) : accessible par son lien, jamais
    // indexé — c'est sa raison d'être.
    if (event.value.is_public === false) return true;
    const blockedStatuses: EventStatus[] = [
      "draft",
      "canceled",
      "archived",
      // "completed" : un événement TERMINÉ ne doit plus être indexé (le backend
      // appelle déjà notifyEventRemoved sur la transition public→completed).
      // Sans ça, la page d'archive restait en index,follow. Karim 2026-06-24.
      "completed",
      // "closed" : billetterie clôturée → plus de vente possible. On ne veut pas
      // ranker une page non-achetable (incohérence prix/CTA). Karim 2026-06-27.
      "closed",
    ];
    return blockedStatuses.includes(event.value.status);
  });

  // robots cohérent avec l'indexabilité globale (staging = noindex) + noindex
  // page si l'événement n'est pas dans un statut indexable.
  const robotsContent = useRobotsContent(shouldNoindex);

  // Min price depuis les ticket_types (pour offers JSON-LD + Rich Snippet).
  const minTicketPrice = computed<number | null>(() => {
    const types = (event.value?.ticket_types || []) as TicketType[];
    const prices = types
      .map((t) => Number(t.price))
      .filter((n) => Number.isFinite(n) && n >= 0);
    if (prices.length === 0) return null;
    return Math.min(...prices);
  });

  // availability JSON-LD basé sur le nombre de ticket_types restants
  const offersAvailability = computed<string>(() => {
    // Terminé OU clôturé : plus de vente possible → SoldOut. Karim 2026-06-27.
    if (event.value?.status === "completed" || event.value?.status === "closed") {
      return "https://schema.org/SoldOut";
    }
    const types = (event.value?.ticket_types || []) as TicketType[];
    if (types.length === 0) return "https://schema.org/InStock";
    // Si tous les ticket_types ont max_quantity atteint → SoldOut
    const anyAvailable = types.some((t) => {
      if (t.max_quantity == null) return true;
      const sold = t.sold_count ?? 0;
      return sold < t.max_quantity;
    });
    return anyAvailable
      ? "https://schema.org/InStock"
      : "https://schema.org/SoldOut";
  });

  // Démo intégrée à l'accueil : la page s'affiche DANS la home, elle ne doit
  // rien écrire dans son <head> (titre, robots, canonique, JSON-LD).
  const resultat = { eventCoverImageId, ogDescription, ogImageUrl, canonicalUrl };
  if (useDemoAccueil().estIntegree) return resultat;

  useHead({
    title: seoTitle,
    bodyAttrs: {
      style: computed(() => event.value ? 'background-color: #f7f9fd' : ''),
    },
    link: [{ rel: "canonical", key: "canonical", href: canonicalUrl.value }],
    meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
    script: computed(() => {
      if (!event.value || shouldNoindex.value) return [];

      // ISO AVEC le décalage du fuseau de l'association (lot 2) : la chaîne
      // naïve de la base (UTC) était lue par Google comme une heure LOCALE —
      // un gala à 19:00 en Martinique annoncé à 23:00.
      const fuseau = fuseauDe(event.value);
      const startDate = isoAvecDecalage(event.value.start_date, fuseau) || event.value.start_date;
      const endDate = event.value.end_date
        ? isoAvecDecalage(event.value.end_date, fuseau) || event.value.end_date
        : null;
      const orgName = event.value.organization_name || "Donoor";
      const orgUrl = event.value.organization_slug
        ? `https://donoor.org/associations/${event.value.organization_slug}`
        : "https://donoor.org";

      const eventStatus =
        event.value.status === "canceled"
          ? "https://schema.org/EventCancelled"
          : event.value.status === "completed"
            ? "https://schema.org/EventCompleted"
            : "https://schema.org/EventScheduled";

      const lieu = lieuEvenementSchema(event.value);
      const eventLd: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.value.title,
        description: ogDescription.value,
        startDate,
        ...(endDate ? { endDate } : {}),
        eventStatus,
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        ...(event.value.cover_image ? { image: ogImageUrl.value } : {}),
        ...(lieu ? { location: lieu } : {}),
        organizer: {
          "@type": "Organization",
          name: orgName,
          url: orgUrl,
        },
        url: canonicalUrl.value,
      };

      // offers : Rich Snippet "Tickets — à partir de X €". Google peut afficher
      // le prix dans la SERP si on fournit price + currency + url + availability.
      if (minTicketPrice.value != null) {
        eventLd.offers = {
          "@type": "Offer",
          price: minTicketPrice.value,
          priceCurrency: "EUR",
          availability: offersAvailability.value,
          url: canonicalUrl.value,
          validFrom: event.value.date_created || startDate,
        };
      }

      // BreadcrumbList : Accueil → Billetterie → Cet événement
      const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t("billetterie.useEventCheckoutSeo.fileAccueil"), item: "https://donoor.org" },
          { "@type": "ListItem", position: 2, name: t("billetterie.useEventCheckoutSeo.fileEvenements"), item: "https://donoor.org/recherche/billetterie" },
          { "@type": "ListItem", position: 3, name: event.value.title, item: canonicalUrl.value },
        ],
      };

      return [
        { type: "application/ld+json", innerHTML: JSON.stringify(eventLd) },
        { type: "application/ld+json", innerHTML: JSON.stringify(breadcrumbLd) },
      ];
    }),
  });

  useSeoMeta({
    title: () => seoTitle.value,
    description: () => ogDescription.value,
    ogType: "website",
    ogTitle: () => event.value?.title || t("billetterie.useEventCheckoutSeo.evenementParDefaut"),
    ogDescription: () => ogDescription.value,
    ogImage: () => ogImageUrl.value,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogUrl: () => canonicalUrl.value,
    ogLocale: () => ogLocale.value,
    twitterCard: "summary_large_image",
    twitterTitle: () => event.value?.title || t("billetterie.useEventCheckoutSeo.evenementParDefaut"),
    twitterDescription: () => ogDescription.value,
    twitterImage: () => ogImageUrl.value,
    robots: () => robotsContent.value,
  });

  return resultat;
}

// Helper local : formatte une date ISO en "15 juin 2026" (FR), jour lu dans
// le fuseau de l'association (lot 2) ; chaîne naïve de la base = UTC.
function formatDateFr(iso: string | null | undefined, fuseau?: string | null): string {
  return formatDateAssociation(iso, fuseau, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
